import { useState, useEffect, useCallback, useMemo } from 'react'
import type { UserData } from '../../types'
import { getCurrentUser } from '../../apiClients/user/userClient'
import { signOut } from '../../apiClients/auth/authClient'
import { authInterceptor } from '../../apiClients/core/interceptors/authInterceptor'
import { wasUserAuthenticated, saveUserToCache, clearUserCache } from '../../utils/userCache'

/**
 * Хук для управления состоянием пользователя
 * 
 * Функции:
 * - Хранение данных пользователя (username, email, avatar, theme, locale)
 * - Сохранение и восстановление данных из localStorage
 * - Загрузка полного профиля с сервера при инициализации
 * - Управление состоянием авторизации
 */
export const useUserState = () => {
  const [user, setUser] = useState<UserData | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Инициализация пользователя
  useEffect(() => {
    initializeUser()
  }, [])

  const initializeUser = async () => {
    if (!wasUserAuthenticated()) {
      setIsLoading(false)
      return
    }

    try {
      const userProfile = await getCurrentUser()
      setUser(userProfile)
      setIsLoggedIn(true)
      saveUserToCache(userProfile)
    } catch (error) {
      console.error('Failed to load user profile:', error)
      clearUserCache()
      setUser(null)
      setIsLoggedIn(false)
    } finally {
      setIsLoading(false)
    }
  }

  const login = useCallback((userData: UserData) => {
    setUser(userData)
    setIsLoggedIn(true)
    saveUserToCache(userData)
  }, [])

  const logout = useCallback(async (callSignOutApi = true) => {
    if (callSignOutApi && isLoggedIn) {
      try {
        await signOut()
      } catch (error) {
        console.error('Logout API call failed:', error)
      }
    }
    
    setUser(null)
    setIsLoggedIn(false)
    clearUserCache()
  }, [isLoggedIn])

  // Настраиваем auth interceptor
  useEffect(() => {
    authInterceptor.setOnAuthFailed(() => {
      console.warn('Authentication failed (refresh token expired) - logging out user')
      logout(false)
    })
  }, [logout])

  const updateUser = useCallback((updates: Partial<UserData>) => {
    setUser((prevUser) => {
      if (!prevUser) return null

      const updatedUser = { 
        ...prevUser, 
        ...updates,
      }
      
      saveUserToCache(updatedUser)
      return updatedUser
    })
  }, [])

  return useMemo(
    () => ({
      user,
      isLoggedIn,
      isLoading,
      login,
      logout,
      updateUser,
    }),
    [user, isLoggedIn, isLoading, login, logout, updateUser]
  )
}
